import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Pause, Play, Sparkles } from "lucide-react";
import { GET_MANAGER_OVERVIEW_INSIGHTS_URL } from "../../../features/UrlHelper";
import { formatInsight, formatObdobie } from "./overviewInsightsText";
import "./OverviewInsights.scss";

// ─── OverviewInsights ─────────────────────────────────────────────────────────
// Zaujímavosti za celú históriu prevádzky -- zámerne nenápadný jednoriadkový
// pruh pod KPI kartami, po jednej zaujímavosti na šípky.
//
// Načítajú sa raz pri otvorení Prehľadu a od dátumového filtra ani trasy
// nezávisia -- preto žiadne props a žiadny realtime odber (backend ráta
// len uzavreté dni, dnešné objednávky ich nemenia). Kým sa načítavajú, pri
// chybe alebo keď nie je čo ukázať, sa nevykreslí nič: je to doplnok, nie
// niečo, na čo má majiteľ čakať.
//
// Automatický posun riadi CSS animácia tenkej linky dole: na jej konci
// (onAnimationEnd) sa prejde na ďalšiu. Hover, fokus aj tlačidlo pauzy ju
// zastavia cez animation-play-state (pauza pokračuje tam, kde skončila) a pri
// prefers-reduced-motion sa vôbec nespustí.
//
// Tlačidlá zámerne NEMAJÚ aria-pressed: test Prehľadu ním hľadá rozsvietenú
// voľbu obdobia.

const SWIPE_PRAH = 40;

export default function OverviewInsights() {
  const [stav, setStav] = useState({ nacitava: true, chyba: false, data: null });
  const [index, setIndex] = useState(0);
  const [smer, setSmer] = useState("dalej");
  const [pozastavene, setPozastavene] = useState(false);
  // Kým nikto neklikol, čítačka obrazovky nemá hlásiť každú automatickú zmenu.
  const [rucne, setRucne] = useState(false);
  const dotykX = useRef(null);

  useEffect(() => {
    let zrusene = false;
    axios
      .get(GET_MANAGER_OVERVIEW_INSIGHTS_URL)
      .then(({ data }) => {
        if (!zrusene) setStav({ nacitava: false, chyba: false, data });
      })
      .catch(() => {
        if (!zrusene) setStav({ nacitava: false, chyba: true, data: null });
      });
    return () => {
      zrusene = true;
    };
  }, []);

  const karty = useMemo(() => {
    const insighty = Array.isArray(stav.data?.insighty) ? stav.data.insighty : [];
    return insighty.map(formatInsight).filter(Boolean);
  }, [stav.data]);

  const pocet = karty.length;
  const aktivny = pocet > 0 ? Math.min(index, pocet - 1) : 0;

  const posun = (krok, { manualne = true } = {}) => {
    if (pocet < 2) return;
    setSmer(krok > 0 ? "dalej" : "spat");
    setIndex((i) => (Math.min(i, pocet - 1) + krok + pocet) % pocet);
    if (manualne) setRucne(true);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      posun(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      posun(-1);
    }
  };

  const onTouchStart = (e) => {
    dotykX.current = e.touches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    const start = dotykX.current;
    dotykX.current = null;
    const koniec = e.changedTouches?.[0]?.clientX;
    if (start == null || koniec == null) return;
    const dx = koniec - start;
    if (Math.abs(dx) >= SWIPE_PRAH) posun(dx < 0 ? 1 : -1);
  };

  if (stav.nacitava || stav.chyba || pocet === 0) return null;

  const karta = karty[aktivny];
  const { Icon } = karta;
  const viac = pocet > 1;

  return (
    <section
      className={`ovw-ins${pozastavene ? " ovw-ins--paused" : ""}`}
      aria-label="Zaujímavosti"
      aria-roledescription="karusel"
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <span className="ovw-ins__mark" title={formatObdobie(stav.data?.obdobie)}>
        <Sparkles size={14} aria-hidden="true" />
      </span>

      <div className="ovw-ins__viewport" aria-live={rucne ? "polite" : "off"}>
        <p
          key={`${karta.kluc}-${aktivny}`}
          className={`ovw-ins__slide ovw-ins__slide--${smer}`}
        >
          <Icon size={14} className={`ovw-ins__icon ovw-ins__icon--${karta.ton}`} aria-hidden="true" />
          <span className="ovw-ins__label">{karta.nadpis}:</span>{" "}
          <strong className="ovw-ins__value">{karta.hodnota}</strong>
          <span className="ovw-ins__desc"> · {karta.popis}</span>
        </p>
      </div>

      {viac && (
        <div className="ovw-ins__controls">
          <span className="ovw-ins__counter" aria-hidden="true">
            {aktivny + 1} / {pocet}
          </span>
          <button
            type="button"
            className="ovw-ins__btn ovw-ins__btn--pause"
            onClick={() => setPozastavene((p) => !p)}
            aria-label={pozastavene ? "Spustiť automatické prepínanie" : "Pozastaviť automatické prepínanie"}
            title={pozastavene ? "Spustiť" : "Pozastaviť"}
          >
            {pozastavene ? <Play size={12} /> : <Pause size={12} />}
          </button>
          <button
            type="button"
            className="ovw-ins__btn"
            onClick={() => posun(-1)}
            aria-label="Predchádzajúca zaujímavosť"
            title="Predchádzajúca zaujímavosť"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            className="ovw-ins__btn"
            onClick={() => posun(1)}
            aria-label="Ďalšia zaujímavosť"
            title="Ďalšia zaujímavosť"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {viac && (
        <span className="ovw-ins__progress" aria-hidden="true">
          <span
            key={`${karta.kluc}-${aktivny}`}
            className="ovw-ins__progress-fill"
            data-testid="ovw-ins-progress"
            onAnimationEnd={() => posun(1, { manualne: false })}
          />
        </span>
      )}
    </section>
  );
}
